import * as PropTypes from 'prop-types';
import {
  DataValue,
  graphql,
} from 'react-apollo';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
  branch,
  compose,
  getContext,
  lifecycle,
  renderComponent,
  withHandlers,
  withState,
} from 'recompose';
import ErrorMessage from '../../components/ErrorMessage';
import LoadingAnimation from '../../components/LoadingAnimation';
import { haveMessageTemplate } from '../../helpers/messenger';
import { getQueryStringValueByKey } from '../../helpers/queryStringUtils';
import { capitalizeFirstLetter } from '../../helpers/stringUtils';
import { checkProfanity } from '../../helpers/fetchTools';
import cache, {
  FIVE_YEARS,
  HELP_CARDS_KEY,
} from '../../services/CacheService';
import { getNoobieStatus } from '../../services/NoobieService';
import { getSession } from '../../services/SessionService';
import { UserSession } from '../../types';
import { getPlaceholder } from '../ConversationsList/ConversationsListActions';
import { mapCustomFields } from '../CustomFields/CustomFieldsActions';
// components
import MessageTemplatesListComponent from './MessageTemplatesListComponent';
import { getFilteredTemplates } from './MessageTemplatesListSelector';
import {
  MessageTemplatesContainerProps,
  MessageTemplatesList as MessageTemplatesListType,
  MessageTemplatesPayload,
  MessageTemplatesVariables,
} from './MessageTemplatesListTypes';
import MessageTemplatesQuery from './MessageTemplatesQuery';

const FIRST_PAGE_INDEX = 1;
const DEFAULT_ITEMS_PER_PAGE = 3;

/* Redux */
const mapStateToProps = (store, props: MessageTemplatesContainerProps): Partial<MessageTemplatesContainerProps> => ({
    customFields: store.customFieldsStore,
});

const mapDispatchers = (dispatch, ownProps: MessageTemplatesContainerProps) => ({
    mapCustomFields(messageTemplates: MessageTemplatesListType) {
        const dynamicFieldInfo = createDynamicFieldInfo(ownProps.session, ownProps.recipient);
        const profanityPromises = Object.keys(dynamicFieldInfo).map( field => {
            return checkProfanity(dynamicFieldInfo[field]);
        });

        Promise.all(profanityPromises).then( res => {
            const checkedFields = res.map( value => {
                return {
                    hasProfanity: value.data.containsProfanity
                };
            });
            const checkedDynamicFields = {};
            Object.keys(dynamicFieldInfo).forEach( (key, index) => checkedDynamicFields[key] = checkedFields[index]);
            dispatch(mapCustomFields(messageTemplates, dynamicFieldInfo, checkedDynamicFields));
        }).catch(err => {
            console.error(err);
            // if profanity service breaks we want to pass all the fields since service will just let messages through if its down.
            const allValid = Object.keys(dynamicFieldInfo).map( () => {
                return {
                    hasProfanity: false
                };
            });
            const allVakidFields = {};
            Object.keys(dynamicFieldInfo).forEach( (key, index) => allVakidFields[key] = allValid[index]);
            dispatch(mapCustomFields(messageTemplates, dynamicFieldInfo, allVakidFields));
        });

        const placeholder = {
            placeholderRoute: `${ownProps.match.url}${ownProps.location.search}`,
            placeholderInfo: ownProps.recipient,
            active: true,
        };

        ownProps.viewContext === 'chatStarters' ? dispatch(getPlaceholder(placeholder)) : null;
    }
});

const createDynamicFieldInfo = (session, recipient) => ({
    customerName: capitalizeFirstLetter(recipient.name ? recipient.name.trim().split(' ')[0] : null),
    salesRepName: capitalizeFirstLetter(session.firstname),
    dealership: recipient.outlet_locator || recipient.dealership_name,
});

/* GraphQL */
// TODO: one day this could be reusable - graphql utils
const renderLoading = (component, propName = 'data') => (
    branch(
        (props: MessageTemplatesContainerProps) => props[propName] && props[propName].loading,
        renderComponent(component)
    )
);

const renderError = (component, propName = 'data') => (
    branch(
        (props: MessageTemplatesContainerProps) => props[propName] && props[propName].error,
        renderComponent(component)
    )
);

const mapTemplates = lifecycle<MessageTemplatesContainerProps, {}>({
    componentWillMount() {
        this.props.mapCustomFields(this.props.data.messageTemplates.templates);
    },
    async componentDidMount() {
        const session: UserSession = await getSession();
        const { details: { conversationNoobieUser: isNoobie }} = await getNoobieStatus(session);
        this.props.setTemplateLanguages(this.props.data.messageTemplates.supportedLanguages);
        const hasSeenCards = await cache.get(HELP_CARDS_KEY);

        if (!hasSeenCards && isNoobie) {
            this.props.setShowCards(true);
            cache.add(HELP_CARDS_KEY, new Date(), FIVE_YEARS);
        }
    },
    componentWillUpdate(nextProps: MessageTemplatesContainerProps) {
        if (this.props.data.messageTemplates !== nextProps.data.messageTemplates) { // FIXME: is this a sufficent equality check? - we might never find out if we are lucky.
            this.props.mapCustomFields(nextProps.data.messageTemplates.templates);
        }
    }
});

const watchTemplates = lifecycle<MessageTemplatesContainerProps, {}>({
    componentWillReceiveProps({ setHaveMessageTemplates, data }) {
        setHaveMessageTemplates && setHaveMessageTemplates(haveMessageTemplate(data.messageTemplates));
    }
});

const checkLanguage = value => {
    if (value && value.length <= 3) {
        return value.toLowerCase();
    }
    return 'en';
};

const getCriteria = (props: MessageTemplatesContainerProps) => ({
    variables: {
        criteria: {
            callListId: getQueryStringValueByKey('calllist_id') && parseInt(getQueryStringValueByKey('calllist_id'), 10),
            conversationId: props.conversationId,
            language: !props.session.features.includes(200) ? null : (!props.activeLanguage ? checkLanguage(props.recipient.language) : props.activeLanguage),
            page: {
                pageNumber: FIRST_PAGE_INDEX,
                quantityRecords: props.itemsPerPage || DEFAULT_ITEMS_PER_PAGE
            }
        }
    }
});

enum FetchModes {
    NEXT = 'next',
    PREVIOUS = 'previous',
}

type FetchMode = FetchModes.NEXT | FetchModes.PREVIOUS;

const updateCriteria = ({ variables, messageTemplates }: DataValue<MessageTemplatesPayload, MessageTemplatesVariables>, flag: FetchMode, activeLanguage: string) => ({
    ...variables,
    criteria: {
        language: activeLanguage,
        ...variables.criteria,
        page: {
            ...variables.criteria.page,
            pageNumber: flag === FetchModes.NEXT ? messageTemplates.pageInfo.current + 1 : messageTemplates.pageInfo.current - 1
        }
    }
});


const MessageTemplatesContainer = compose(
    withRouter,
    getContext({
        session: PropTypes.any.isRequired
    }),
    graphql(MessageTemplatesQuery, {
        options: getCriteria,
        props({ data, ownProps }) {
            return {
                data: getFilteredTemplates(data, ownProps),
                loadMoreData() {
                    data.fetchMore({
                        variables: updateCriteria(data, FetchModes.NEXT, ownProps.session.features.includes(200) ? ownProps.activeLanguage : null),
                        updateQuery: (previousResult, { fetchMoreResult }) => ({
                            ...previousResult,
                            messageTemplates: {
                                ...previousResult.messageTemplates,
                                templates: [...previousResult.messageTemplates.templates, ...fetchMoreResult.messageTemplates.templates],
                                pageInfo: {
                                    ...previousResult.messageTemplates.pageInfo,
                                    ...fetchMoreResult.messageTemplates.pageInfo
                                }
                            }
                        })
                    });
                },
                loadPrevPage() {
                    data.fetchMore({
                        variables: updateCriteria(data, FetchModes.PREVIOUS, ownProps.activeLanguage),
                        updateQuery: (previousResult, { fetchMoreResult }) => fetchMoreResult
                    });
                },
                loadNextPage() {
                    data.fetchMore({
                        variables: updateCriteria(data, FetchModes.NEXT, ownProps.activeLanguage),
                        updateQuery: (previousResult, { fetchMoreResult }) => fetchMoreResult
                    });
                }
            };
        }
    }),
    renderLoading(LoadingAnimation),
    renderError(ErrorMessage),
    connect(mapStateToProps, mapDispatchers),
    withState('activeMessageTemplate', 'setMessageTemplateState', null),
    withState('showCards', 'setShowCards', false),
    withState('templateLanguages', 'setTemplateLanguages', []),
    withHandlers({
        selectMessageTemplate: ({ activeMessageTemplate, setMessageTemplateState }) => (templateId?: string) => {
            if (activeMessageTemplate && templateId) {
                return false;
            }
            setMessageTemplateState(templateId);
        }
    }),
    mapTemplates,
    watchTemplates,
    // FIXME: investigate performance hit of second branch condition
    // for now, we need the first branch to ensure that data is loaded before we map message templates
    branch(({ customFields }) => !customFields.customFieldsLoaded, renderComponent(LoadingAnimation))
);

export default MessageTemplatesContainer(MessageTemplatesListComponent) as React.ComponentClass<Partial<MessageTemplatesContainerProps>>;



// WEBPACK FOOTER //
// ./src/sections/MessageTemplatesList/index.ts